import { useMemo, useState } from 'react'
import { useCart } from './cartContext'
import { writeCheckoutGuestCartId } from './cartPersistence'
import { useCustomerAuth } from '../customer-auth'
import { useShopI18n } from '../i18n'
import { CartDrawerItems } from './cart-drawer/CartDrawerItems'
import { CartDrawerFooter } from './cart-drawer/CartDrawerFooter'
import { useCartDrawerEffects } from './cart-drawer/useCartDrawerEffects'

const CartDrawer = () => {
  const { t, formatCurrency, toLocalizedPath } = useShopI18n()
  const { authenticated } = useCustomerAuth()
  const {
    cart,
    cartId,
    itemCount,
    subtotal,
    initializing,
    busy,
    error,
    clearError,
    updateItemQuantity,
    removeItem,
    drawerOpen,
    closeDrawer,
  } = useCart()
  const [activeItemId, setActiveItemId] = useState<string | null>(null)
  const checkoutPath = toLocalizedPath('/checkout')
  const loginRedirectPath = `${toLocalizedPath('/dashboard/login')}?intent=checkout&next=${encodeURIComponent(checkoutPath)}`

  const sortedItems = useMemo(
    () => [...(cart?.items ?? [])].sort((left, right) => left.productName.localeCompare(right.productName)),
    [cart?.items],
  )

  const withItemBusy = async (itemId: string, operation: () => Promise<boolean>) => {
    setActiveItemId(itemId)
    try {
      await operation()
    } finally {
      setActiveItemId((current) => (current === itemId ? null : current))
    }
  }

  useCartDrawerEffects({ drawerOpen, closeDrawer })

  return (
    <div
      className={`fixed inset-0 z-[120] transition-opacity duration-200 ${
        drawerOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      }`}
      aria-hidden={!drawerOpen}
      inert={!drawerOpen}
    >
      <button
        type="button"
        aria-label={t('common.close')}
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
        onClick={closeDrawer}
      />
      <aside
        className={`absolute right-0 top-0 flex h-full w-full max-w-[420px] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-out will-change-transform ${
          drawerOpen ? 'translate-x-0' : 'translate-x-6'
        }`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-xl font-bold text-slate-900">{t('cart.title')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            title={t('common.close')}
            className="grid h-9 w-9 place-items-center rounded-lg border border-slate-300 text-slate-700 transition hover:bg-slate-50"
            onClick={closeDrawer}
          >
            <svg aria-hidden viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-2">
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {error && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <p>{error}</p>
            </div>
          )}

          {initializing && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              {t('cart.loading')}
            </div>
          )}

          {!initializing && sortedItems.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-5 text-center">
              <p className="text-sm font-semibold text-slate-900">{t('cart.emptyTitle')}</p>
              <p className="mt-1 text-xs text-slate-600">{t('cart.emptyDescription')}</p>
            </div>
          )}

          {!initializing && sortedItems.length > 0 && (
            <CartDrawerItems
              sortedItems={sortedItems}
              busy={busy}
              activeItemId={activeItemId}
              formatCurrency={formatCurrency}
              removeLabel={t('cart.remove')}
              withItemBusy={withItemBusy}
              updateItemQuantity={updateItemQuantity}
              removeItem={removeItem}
            />
          )}
        </div>

        <CartDrawerFooter
          itemCount={itemCount}
          subtotal={subtotal}
          busy={busy}
          checkoutBusy={false}
          formatCurrency={formatCurrency}
          totalQuantityLabel={t('cart.totalQuantity')}
          totalPriceLabel={t('customer.orders.total')}
          checkoutBusyLabel={t('cart.checkoutBusy')}
          checkoutLabel={t('cart.checkout')}
          continueShoppingLabel={t('cart.continueShopping')}
          onCheckout={() => {
            closeDrawer()
            if (!authenticated) {
              if (cartId) writeCheckoutGuestCartId(cartId)
              window.location.assign(loginRedirectPath)
              return
            }
            if (!cartId) return
            window.location.assign(checkoutPath)
          }}
          onContinueShopping={() => {
            clearError()
            closeDrawer()
          }}
        />
      </aside>
    </div>
  )
}

export default CartDrawer
